import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthShell, { authStyles } from "../../../components/usuarios_tienda/AuthShell";
import PasswordFields from "../../../components/usuarios_tienda/PasswordFields";
import { validarReset, restablecerPassword } from "../../../services/usuarios_tienda/invitaciones";
import { isStrongPasswordValid, STRONG_PASSWORD_ERROR } from "../../../utils/passwordRules";

const LOGIN_PATH = "/administracion/tienda/login";

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState("loading"); // loading | ready | invalid | done
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) {
      setStatus("invalid");
      setError("Falta el token en el enlace.");
      return;
    }
    validarReset(token)
      .then((data) => {
        if (!active) return;
        setInfo(data);
        setStatus("ready");
      })
      .catch((err) => {
        if (!active) return;
        setStatus("invalid");
        setError(err?.message || "El enlace es inválido o ha vencido.");
      });
    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!isStrongPasswordValid(password)) {
      setError(STRONG_PASSWORD_ERROR);
      return;
    }
    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setSubmitting(true);
    try {
      await restablecerPassword({ token, password, confirmPassword });
      setStatus("done");
    } catch (err) {
      setError(err?.message || "No se pudo restablecer la contraseña.");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") {
    return <AuthShell title="Validando enlace…" subtitle="Un momento por favor." />;
  }

  if (status === "invalid") {
    return (
      <AuthShell title="Enlace no válido" subtitle="El enlace es inválido o ha vencido.">
        <div style={{ ...authStyles.alert, ...authStyles.alertError }}>{error}</div>
        <button style={authStyles.primaryBtn} onClick={() => navigate(LOGIN_PATH)}>
          Ir al inicio de sesión
        </button>
      </AuthShell>
    );
  }

  if (status === "done") {
    return (
      <AuthShell title="Contraseña actualizada" subtitle="Tu contraseña se cambió correctamente.">
        <div style={{ ...authStyles.alert, ...authStyles.alertOk }}>
          Ya puedes iniciar sesión con tu nueva contraseña.
        </div>
        <button style={authStyles.primaryBtn} onClick={() => navigate(LOGIN_PATH)}>
          Iniciar sesión
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      badge={info?.tiendaNombre || "Restablecer"}
      title="Restablecer contraseña"
      subtitle="Crea una nueva contraseña para tu cuenta."
    >
      <div style={authStyles.readonlyBox}>
        <div><strong>{info?.nombre}</strong></div>
        <div style={{ color: "#6b7280" }}>{info?.correo}</div>
      </div>

      {error && <div style={{ ...authStyles.alert, ...authStyles.alertError }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <PasswordFields
          password={password}
          confirmPassword={confirmPassword}
          onChangePassword={setPassword}
          onChangeConfirm={setConfirmPassword}
          passwordLabel="Nueva contraseña"
        />
        <button type="submit" style={{ ...authStyles.primaryBtn, opacity: submitting ? 0.8 : 1 }} disabled={submitting}>
          {submitting ? "Guardando…" : "Guardar contraseña"}
        </button>
      </form>
    </AuthShell>
  );
}

export default ResetPasswordPage;
